import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { useTheme } from '../contexts/ThemeContext';
import { GraduationCap, Target, Users, Award, Lightbulb, Heart } from 'lucide-react';

export default function About() {
  const { theme } = useTheme();
  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const values = [
    { icon: Lightbulb, title: 'Innovation', description: 'We embrace new technologies and creative approaches to art education.' },
    { icon: Heart, title: 'Passion', description: 'Our instructors bring years of real-world experience and genuine love for their craft.' },
    { icon: Users, title: 'Community', description: 'Learning is better together. Join a vibrant community of artists and creators.' },
    { icon: Target, title: 'Excellence', description: 'We maintain the highest standards in curriculum design and student support.' }
  ];

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary flex items-center justify-center">
            <GraduationCap className="w-10 h-10 text-primary-foreground" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            About Visual Institute
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Visual Institute of Arts is a premier online learning platform dedicated to nurturing 
            creative talent. We believe that art education should be accessible, engaging, and 
            transformative for everyone regardless of their background or location.
          </p>
        </motion.div>

        {/* Mission */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
              Our Mission
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To democratize art education by providing world-class instruction through an 
              innovative, engaging, and accessible online platform. We aim to empower aspiring 
              artists with the skills, knowledge, and confidence they need to pursue their 
              creative passions.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Through our gamified learning experience, AI-powered tutoring, and vibrant 
              community, we're redefining what online art education can be.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <img
              src="https://images.unsplash.com/photo-1573761449626-1b80629dafc3"
              alt="Creative studio"
              className="rounded-2xl w-full aspect-video object-cover"
            />
          </motion.div>
        </div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-12">
            Our Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <Card key={i} className={cardClass}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Stats */}
        <div className="bg-muted/30 rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '10K+', label: 'Students Worldwide' },
              { value: '200+', label: 'Courses Available' },
              { value: '50+', label: 'Expert Instructors' },
              { value: '95%', label: 'Student Satisfaction' }
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
